import { useMutation, useQueryClient } from '@tanstack/react-query';

import { addFavorite, removeFavorite } from '@libs/api-proxy-client';
import { AccountProduct } from '@libs/common-types';
import { ResponseWithParsedData } from '@libs/core';

import { FavoriteItemsSuccessResponse } from '../../types';
import { FAVORITES_QUERY_KEY } from './queryKey';

type FavoriteResponseType = Error | FavoriteItemsSuccessResponse | ResponseWithParsedData<FavoriteItemsSuccessResponse>;

type MutateProps = {
    sku: string;
    userId: string;
    action: 'add' | 'remove';
};

type MutateContext = {
    prevData: FavoriteItemsSuccessResponse | undefined;
    queryKey: readonly [string, string];
};

type MutateResult = {
    response: FavoriteResponseType;
};

type UseToggleFavoriteProps = {
    onAddActionCallback?: () => void;
    onErrorCallback?: (props: { error: Error; event: 'add' | 'remove' }) => void;
};

const throwIfFavoriteMutationFailed = (response: FavoriteResponseType, action: MutateProps['action']) => {
    if (response instanceof Error) {
        throw response;
    }

    // apiFetch returns failed HTTP calls as ResponseWithParsedData (resolved, not thrown).
    if (response instanceof Response && !response.ok) {
        const { parsedResponse } = response;
        if (
            parsedResponse &&
            typeof parsedResponse === 'object' &&
            'detail' in parsedResponse &&
            typeof (parsedResponse as { detail: unknown }).detail === 'string'
        ) {
            throw new Error((parsedResponse as { detail: string }).detail);
        }

        throw new Error(`Failed to ${action} favorite`);
    }
};

const extractFavoriteResponse = (response: FavoriteResponseType): FavoriteItemsSuccessResponse | null => {
    if (response instanceof Error) {
        return null;
    }
    if (response && typeof response === 'object' && 'parsedResponse' in response) {
        const parsed = (response as { parsedResponse: unknown }).parsedResponse;
        if (parsed && typeof parsed === 'object' && 'items' in parsed) {
            return parsed as FavoriteItemsSuccessResponse;
        }
        return null;
    }
    if (response && 'items' in response) {
        return response;
    }
    return null;
};

const extractItemsFromCacheData = (data: unknown): AccountProduct[] => {
    if (!data || typeof data !== 'object') {
        return [];
    }
    // Handle error responses (e.g., 401 unauthorized)
    if ('parsedResponse' in data) {
        const parsed = (data as { parsedResponse: unknown }).parsedResponse;
        if (
            parsed &&
            typeof parsed === 'object' &&
            'items' in parsed &&
            Array.isArray((parsed as { items: unknown }).items)
        ) {
            return (parsed as FavoriteItemsSuccessResponse).items;
        }
        return [];
    }
    // Handle success response with items
    if ('items' in data && Array.isArray((data as { items: unknown }).items)) {
        return (data as FavoriteItemsSuccessResponse).items;
    }
    return [];
};

export const useToggleFavorite = ({ onAddActionCallback, onErrorCallback }: UseToggleFavoriteProps = {}) => {
    const queryClient = useQueryClient();
    const requestFn = { add: addFavorite, remove: removeFavorite };

    return useMutation<MutateResult, Error, MutateProps, MutateContext>({
        onMutate: async ({ action, sku, userId }: MutateProps) => {
            const queryKey = [FAVORITES_QUERY_KEY, userId] as const;

            await queryClient.cancelQueries({ queryKey });

            const prevData = queryClient.getQueryData<FavoriteItemsSuccessResponse>(queryKey);

            queryClient.setQueryData<FavoriteItemsSuccessResponse>(queryKey, old => {
                // Safely extract items from cache data (handles error responses, undefined, etc.)
                const items = extractItemsFromCacheData(old);

                if (action === 'add') {
                    const exists = items.some(item => item.sku === sku);
                    if (exists) {
                        // Return the original structure if it exists, otherwise create new structure
                        return old && typeof old === 'object' && 'items' in old ? old : { items };
                    }
                    return { items: [...items, { sku } as AccountProduct] };
                } else {
                    return { items: items.filter(item => item.sku !== sku) };
                }
            });

            return { prevData, queryKey };
        },
        mutationFn: async ({ action, sku }: MutateProps) => {
            const response = await requestFn[action]({ sku });

            // Throw on Error and failed HTTP ResponseWithParsedData so optimistic state
            // rolls back and onErrorCallback (e.g. PDP snackbar) runs.
            throwIfFavoriteMutationFailed(response, action);

            if (action === 'add' && onAddActionCallback) {
                onAddActionCallback();
            }

            return { response };
        },
        onError: (err, vars, ctx) => {
            if (ctx?.prevData !== undefined) {
                queryClient.setQueryData<FavoriteItemsSuccessResponse>(ctx.queryKey, ctx.prevData);
            }
            if (onErrorCallback) {
                onErrorCallback({ error: err, event: vars.action });
            }
        },
        onSettled: (data, _err, _vars, context) => {
            if (!data?.response || !context?.queryKey) return;

            const favoriteResponse = extractFavoriteResponse(data.response);
            if (favoriteResponse) {
                queryClient.setQueryData<FavoriteItemsSuccessResponse>(context.queryKey, favoriteResponse);
            }
        },
    });
};
